import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Send,
  Loader2,
  CheckCircle,
  Award,
  Upload,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const regionOptions = [
  "Georgetown", "Mahaica", "East Coast Demerara", "West Coast Demerara",
  "East Bank Demerara", "West Bank Demerara", "Berbice", "Essequibo",
  "Linden", "Bartica", "Other",
];

const serviceOptions = [
  "Facials", "Nails", "Lash Extensions", "Body Waxing",
  "Hair Services", "Massage", "Brow Services", "Wig Services",
  "Candle Making", "Soap Making", "Body Butter & Scrub",
];

const courseOptions = [
  "Facial Training", "Manicure & Pedicure", "Acrylic Nails",
  "Lash Extensions", "Lash Lift", "Body Waxing", "Hair Services",
  "Advanced Hair", "Brow Training", "Wig Master Class",
  "Candle Making", "Soap Making", "Body Butter & Scrub",
];

export const AlumniApplyPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    whatsapp: "",
    instagram: "",
    photo_url: "",
    location: "",
    region: "",
    services_offered: [],
    courses_completed: [],
    year_completed: "",
    bio: "",
    business_name: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter((i) => i !== item)
        : [...prev[field], item],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.services_offered.length === 0) {
      toast.error("Please select at least one service you offer");
      return;
    }
    if (form.courses_completed.length === 0) {
      toast.error("Please select at least one course you completed");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/alumni/apply`, form);
      if (res.data.success) {
        setIsSubmitted(true);
        toast.success("Application submitted!");
      }
    } catch (error) {
      toast.error("Failed to submit application", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pt-20">
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#121212] border border-[#262626] p-12 text-center"
              data-testid="alumni-apply-success"
            >
              <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
              <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
                Application Submitted
              </h3>
              <p className="font-body text-[#A3A3A3] mb-8">
                Thank you for applying! We'll review your application and contact you once it's approved.
                Your profile will then be visible in our alumni directory.
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="alumni-apply-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <Award className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-4">
              Join the Alumni<br />
              <span className="gold-gradient">Directory</span>
            </h1>
            <p className="font-body text-sm text-[#A3A3A3] max-w-lg mx-auto">
              As a Luxury Beauty graduate, get listed in our directory and receive
              client referrals through our website. Grow your business with us.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-8 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="space-y-8">

            {/* Personal Info */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                Personal Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Full Name *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required
                    data-testid="alumni-name" placeholder="Your full name" className="w-full luxury-input" />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Business Name</label>
                  <input type="text" name="business_name" value={form.business_name} onChange={handleChange}
                    data-testid="alumni-business" placeholder="Your business name (optional)" className="w-full luxury-input" />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Phone *</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} required
                    data-testid="alumni-phone" placeholder="Phone number" className="w-full luxury-input" />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">WhatsApp</label>
                  <input type="tel" name="whatsapp" value={form.whatsapp} onChange={handleChange}
                    data-testid="alumni-whatsapp" placeholder="WhatsApp number (optional)" className="w-full luxury-input" />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Email</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange}
                    data-testid="alumni-email" placeholder="Email (optional)" className="w-full luxury-input" />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Instagram</label>
                  <input type="text" name="instagram" value={form.instagram} onChange={handleChange}
                    data-testid="alumni-instagram" placeholder="@yourusername (optional)" className="w-full luxury-input" />
                </div>
              </div>

              <div className="mt-6">
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Profile Photo URL</label>
                <input type="url" name="photo_url" value={form.photo_url} onChange={handleChange}
                  data-testid="alumni-photo" placeholder="Link to your photo (Instagram, Google Drive, etc.)" className="w-full luxury-input" />
                <p className="font-body text-[10px] text-[#A3A3A3] mt-1">
                  Paste a link to your professional photo. You can use an Instagram post link or Google Drive image link.
                </p>
              </div>

              <div className="mt-6">
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">About You</label>
                <textarea name="bio" value={form.bio} onChange={handleChange} rows={3}
                  data-testid="alumni-bio" placeholder="Tell potential clients about yourself and your experience..."
                  className="w-full luxury-input resize-none" maxLength={500} />
                <p className="font-body text-[10px] text-[#A3A3A3] mt-1">{form.bio.length}/500 characters</p>
              </div>
            </div>

            {/* Location */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                Location
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">City / Town *</label>
                  <input type="text" name="location" value={form.location} onChange={handleChange} required
                    data-testid="alumni-location" placeholder="e.g., Georgetown, Mahaica" className="w-full luxury-input" />
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Region *</label>
                  <select name="region" value={form.region} onChange={handleChange} required
                    data-testid="alumni-region" className="w-full luxury-input">
                    <option value="">Select region</option>
                    {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Services Offered */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-2">
                Services You Offer *
              </h2>
              <p className="font-body text-xs text-[#A3A3A3] mb-4">Select all that apply</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {serviceOptions.map((service) => {
                  const selected = form.services_offered.includes(service);
                  return (
                    <button
                      key={service}
                      type="button"
                      onClick={() => toggleArrayItem("services_offered", service)}
                      data-testid={`alumni-service-${service.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`p-3 border text-left text-xs font-body transition-all duration-300 ${
                        selected
                          ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                          : "border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50"
                      }`}
                    >
                      {service}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Courses Completed */}
            <div className="bg-[#121212] border border-[#262626] p-6 md:p-8">
              <h2 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-2">
                Courses Completed at Luxury Beauty *
              </h2>
              <p className="font-body text-xs text-[#A3A3A3] mb-4">Select all courses you've completed</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {courseOptions.map((course) => {
                  const selected = form.courses_completed.includes(course);
                  return (
                    <button
                      key={course}
                      type="button"
                      onClick={() => toggleArrayItem("courses_completed", course)}
                      data-testid={`alumni-course-${course.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`p-3 border text-left text-xs font-body transition-all duration-300 ${
                        selected
                          ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                          : "border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50"
                      }`}
                    >
                      {course}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4">
                <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Year Completed</label>
                <input type="text" name="year_completed" value={form.year_completed} onChange={handleChange}
                  data-testid="alumni-year" placeholder="e.g., 2025" className="w-full luxury-input max-w-[200px]" />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="alumni-apply-submit"
              className="w-full bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase py-4 hover:brightness-110 transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-5 h-5" /> Submit Application</>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default AlumniApplyPage;
